import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Form, Modal, Icon } from 'semantic-ui-react';
import { PositionedModal } from '../../../common/components/PositionedModal';
import UpgradeBanner, { UPGRADE_BANNER_TIERS } from 'common/components/UpgradeBanner';
import { StyledButton } from 'common/components/button/StyledButton';
import { useToasts } from 'common/toast';
import TriggerType from '../../settings/repository-management/types/TriggerType';
import DistributionMethod from '../../settings/repository-management/types/DistributionMethod';
import { useSelector } from 'react-redux';
import { getCurrentOrganization, getCurrentOrganizationMember, isCurrentMemberTeamLeader } from 'common/selectors/auth';
import { H2, H3, Title } from 'common/styles/page';
import { isUndefined } from 'lodash';
import styled from 'styled-components';
import { useGetAllTeamsQuery, useGetEmployeeTeamsQuery } from '../../team-management/api/teamApi';
import type { Team, EmployeeTeam } from '../../team-management/types';
import {
  useApiAdminV1RepositoryRepositoryFileShareCreateMutation,
  useApiAdminV1RepositoryRepositoryFileShareShareableEmployeesRetrieveQuery,
} from '../../../store/generated/admin/repository-file-share';

const SelectionGrid = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 2px;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
`;

const SelectionColumn = styled.div`
  flex: 1 1 240px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  min-height: 500px;
  max-width: 100%;

  @media (max-width: 960px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

const SelectionCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 340px;
  max-height: 500px;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  overflow: hidden;
  background: #ffffff;
`;

const SelectionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e6e6e6;
  background: #fafafa;
`;

const FilterInput = styled.input`
  flex: 1;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #dcdcdc;
  min-height: 38px;
`;

const FilterControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px 14px;
`;

const UserList = styled.div`
  flex: 1;
  min-height: 280px;
  max-height: 430px;
  overflow: auto;
`;

const UserRow = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid #efefef;
  min-height: 36px;
  position: relative;
  color: inherit;

  &:hover {
    background: #f3f4f6;
    transform: translateX(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: 100%;
`;

const UserName = styled(H3)`
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectionTitle = styled(H2)`
  margin: 0;
  color: #111827;
`;

const UserCount = styled.span`
  color: #6b7280;
  font-size: 0.85rem;
`;

const ActionBadge = styled.div<{ variant: 'add' | 'remove' }>`
  min-width: 28px;
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 1rem;
  color: #ffffff;
  background: ${props => (props.variant === 'add' ? '#2d70e2' : '#6b7280')};
`;



type ShareableUser = {
  id: number;
  name: string;
  email?: string;
};

type ShareableEmployeeType = {
  id: number;
  name: string;
};

type KnowledgeBaseContentShareModalProps = {
  fileId: number | string;
  fileName: string;
  isOpen: boolean;
  setModalOpen: (open: boolean) => void;
};

const KnowledgeBaseContentShareModal = (props: KnowledgeBaseContentShareModalProps) => {
  const { isOpen, setModalOpen, fileId, fileName } = props;
  const numericFileId = typeof fileId === 'string' ? Number(fileId) : fileId;
  const { addToast } = useToasts();
  const currentOrganization = useSelector(getCurrentOrganization);
  const currentOrganizationMember = useSelector(getCurrentOrganizationMember);
  const selectedTriggerType = TriggerType.MANUAL;
  const selectedDistributionMethod = DistributionMethod.EMAIL;
  const isTierOneOrganization = currentOrganization?.tier === 1;
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [availableFilter, setAvailableFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [personalMessage, setPersonalMessage] = useState('');
  const selectedOrganization = currentOrganization?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: employeeData, isSuccess: isEmployeesLoaded } =
    useApiAdminV1RepositoryRepositoryFileShareShareableEmployeesRetrieveQuery(
      {
        distributionMethod: selectedDistributionMethod,
        triggerType: selectedTriggerType,
        organizationId: selectedOrganization ?? 0,
      },
      {
        skip: isUndefined(selectedOrganization),
      }
    );

  const [submit] = useApiAdminV1RepositoryRepositoryFileShareCreateMutation();

  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const currentMember = currentOrganizationMember;
  const { data: allTeamsData } = useGetAllTeamsQuery(undefined, { skip: !isOpen });

  const teamsData = useMemo(() => {
    if (!allTeamsData?.results) {
      return allTeamsData;
    }

    if (!isTeamLeader || !currentMember) {
      return allTeamsData;
    }

    const allTeams = allTeamsData.results;
    const currentMemberId = currentMember?.id;
    const directLeaderTeamIds = new Set(
      allTeams
        .filter((team: Team) =>
          team.leader_id === currentMemberId || team.leader === currentMemberId
        )
        .map((team: Team) => team.id)
    );

    if (directLeaderTeamIds.size === 0) {
      return {
        ...allTeamsData,
        count: 0,
        next: null,
        previous: null,
        results: [],
      };
    }

    const childMap = new Map<number, number[]>();
    allTeams.forEach((team: Team) => {
      const parentId = team.parent_team ?? null;
      if (parentId) {
        childMap.set(parentId, [...(childMap.get(parentId) || []), team.id]);
      }
    });

    const managedTeamIds = new Set<number>(directLeaderTeamIds);
    const stack = [...directLeaderTeamIds];

    while (stack.length) {
      const teamId = stack.pop() as number;
      const children = childMap.get(teamId) || [];
      children.forEach((childId) => {
        if (!managedTeamIds.has(childId)) {
          managedTeamIds.add(childId);
          stack.push(childId);
        }
      });
    }

    return {
      ...allTeamsData,
      results: allTeams.filter((team: Team) => managedTeamIds.has(team.id)),
    };
  }, [allTeamsData, currentMember, isTeamLeader]);

  const { data: teamMembersData } = useGetEmployeeTeamsQuery(
    { team_id: selectedTeamId ?? undefined, active_only: true },
    { skip: selectedTeamId == null }
  );

  const teamOptions = useMemo(
    () => [
      { key: 'all', text: isTeamLeader ? 'All managed teams' : 'All teams', value: '' },
      ...(teamsData?.results ?? []).map((team: Team) => ({
        key: String(team.id),
        text: team.name,
        value: team.id,
      })),
    ],
    [teamsData, isTeamLeader]
  );

  const selectedTeamMemberIds = useMemo<Set<number> | undefined>(() => {
    if (!selectedTeamId || !teamMembersData?.results) {
      return undefined;
    }
    return new Set(teamMembersData.results.map((member: EmployeeTeam) => member.employee_id));
  }, [selectedTeamId, teamMembersData]);

  const availableUsers = useMemo<ShareableUser[]>(() => {
    if (!isEmployeesLoaded || employeeData === undefined) {
      return [];
    }

    const employees = Array.isArray(employeeData)
      ? (employeeData as ShareableEmployeeType[])
      : [];

    return employees
      .map(employee => ({
        id: employee.id,
        name: employee.name,
        email: undefined,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employeeData, isEmployeesLoaded]);

  const selectedUsers = useMemo(
    () => availableUsers.filter((user: ShareableUser) => selectedUserIds.has(user.id)),
    [availableUsers, selectedUserIds]
  );

  const availableFilteredUsers = useMemo(() => {
    const query = availableFilter.trim().toLowerCase();
    return availableUsers
      .filter((user: ShareableUser) => !selectedUserIds.has(user.id))
      .filter((user: ShareableUser) =>
        !selectedTeamId || !selectedTeamMemberIds || selectedTeamMemberIds.has(user.id)
      )
      .filter((user: ShareableUser) =>
        !query ||
        user.name.toLowerCase().includes(query) ||
        (user.email ?? '').toLowerCase().includes(query)
      );
  }, [availableUsers, selectedUserIds, availableFilter, selectedTeamId, selectedTeamMemberIds]);

  const selectedFilteredUsers = useMemo(() => {
    const query = selectedFilter.trim().toLowerCase();
    return selectedUsers.filter((user: ShareableUser) =>
      !query ||
      user.name.toLowerCase().includes(query) ||
      (user.email ?? '').toLowerCase().includes(query)
    );
  }, [selectedUsers, selectedFilter]);

  const addUser = (id: number) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const removeUser = (id: number) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const addAllUsers = () => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      availableFilteredUsers.forEach((user: ShareableUser) => next.add(user.id));
      return next;
    });
  };

  const removeAllUsers = () => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      selectedFilteredUsers.forEach((user: ShareableUser) => next.delete(user.id));
      return next;
    });
  };

  const handleClose = () => {
    setSelectedUserIds(new Set());
    setAvailableFilter('');
    setSelectedFilter('');
    setCurrentStep(1);
    setPersonalMessage('');
    setModalOpen(false);
  };

  const handleNext = () => {
    setCurrentStep(2);
  };

  const handleShare = () => {
    setIsSubmitting(true);
    submit({
      body: {
        file_id: numericFileId,
        distribution_method: selectedDistributionMethod,
        trigger_type: selectedTriggerType,
        organization_id: selectedOrganization ?? 0,
        employee_ids: Array.from(selectedUserIds),
        note: personalMessage,
      },
    })
      .unwrap()
      .then((response: any) => {
        const emailsSent = response?.emails_sent ?? 0;
        const emailsFailed = response?.emails_failed ?? 0;

        if (emailsFailed === 0 && emailsSent > 0) {
          addToast(
            `Content shared successfully! ${emailsSent} email${emailsSent !== 1 ? 's' : ''} sent.`,
            { appearance: 'success', autoDismiss: true }
          );
        } else if (emailsFailed === 0 && emailsSent === 0) {
          addToast(
            'Content share link created, but no email was sent. Please verify the recipient and email settings.',
            { appearance: 'warning', autoDismiss: true, autoDismissTimeout: 8000 }
          );
        } else {
          addToast(
            `Content shared with warnings. ${emailsSent} email${emailsSent !== 1 ? 's' : ''} sent, ${emailsFailed} failed. Check logs for details.`,
            { appearance: 'warning', autoDismiss: true, autoDismissTimeout: 8000 }
          );
        }
        handleClose();
      })
      .catch((error: any) => {
        const errorMsg = error?.data?.error || error?.data?.message || 'Failed to share content';
        addToast(errorMsg, { appearance: 'error', autoDismiss: true });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <PositionedModal
      open={isOpen}
      onClose={handleClose}
      size="large"
      closeIcon
      dimmer={{ style: { zIndex: 11000 } }}
      style={{ width: 'min(94vw, 1320px)', maxHeight: 'calc(100vh - 120px)' }}
    >
      <Modal.Header>
        <Title>Share {fileName}</Title>
      </Modal.Header>
      <Modal.Content style={{ minWidth: 0, width: '100%', maxWidth: '100%', minHeight: 260, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {isTierOneOrganization ? (
          <UpgradeBanner
            heading="Resource sharing is a power feature!"
            tier={UPGRADE_BANNER_TIERS.Improve}
          />
        ) : (
          <Form>
            {currentStep === 1 ? (
              <SelectionGrid>
                <SelectionColumn>
                  <SelectionCard>
                    <SelectionCardHeader>
                      <SelectionTitle>Available Recipients</SelectionTitle>
                      <UserCount>{availableFilteredUsers.length} available</UserCount>
                    </SelectionCardHeader>
                    <FilterControls>
                      <Dropdown
                        compact
                        selection
                        options={teamOptions}
                        value={selectedTeamId ?? ''}
                        onChange={(_, { value }) => setSelectedTeamId(value === '' ? null : Number(value))}
                        placeholder="All teams"
                        disabled={isUndefined(selectedOrganization)}
                        style={{ minWidth: 180, width: 180 }}
                      />
                      <FilterInput
                        placeholder="Filter available recipients"
                        value={availableFilter}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAvailableFilter(event.target.value)}
                        disabled={isUndefined(selectedOrganization)}
                      />
                    </FilterControls>
                    <UserList>
                      {isUndefined(selectedOrganization) ? (
                        <div style={{ padding: 14, color: '#6b7280' }}>Please select an organization first.</div>
                      ) : availableFilteredUsers.length === 0 ? (
                        <div style={{ padding: 14, color: '#6b7280' }}>No available recipients.</div>
                      ) : (
                        availableFilteredUsers.map(user => (
                          <UserRow
                            key={user.id}
                            type="button"
                            onClick={() => addUser(user.id)}
                          >
                            <UserMeta>
                              <UserName>{user.name}</UserName>
                            </UserMeta>
                            <ActionBadge variant="add">+</ActionBadge>
                          </UserRow>
                        ))
                      )}
                    </UserList>
                  </SelectionCard>
                </SelectionColumn>

                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, width: 64 }}>
                  <StyledButton
                    label="≫"
                    width="34px"
                    height="34px"
                    color="blue"
                    onClick={addAllUsers}
                    disabled={availableFilteredUsers.length === 0 || isUndefined(selectedOrganization)}
                    aria-label="Add all available recipients"
                  />
                  <StyledButton
                    label="≪"
                    width="34px"
                    height="34px"
                    color="gray"
                    onClick={removeAllUsers}
                    disabled={selectedFilteredUsers.length === 0}
                    aria-label="Remove all share recipients"
                  />
                </div>

                <SelectionColumn>
                  <SelectionCard>
                    <SelectionCardHeader>
                      <SelectionTitle>Share with...</SelectionTitle>
                      <UserCount>{selectedFilteredUsers.length} selected</UserCount>
                    </SelectionCardHeader>
                    <div style={{ padding: '12px 14px' }}>
                      <FilterInput
                        placeholder="Filter share recipients"
                        value={selectedFilter}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedFilter(event.target.value)}
                        disabled={selectedFilteredUsers.length === 0}
                      />
                    </div>
                    <UserList>
                      {selectedFilteredUsers.length === 0 ? (
                        <div style={{ padding: 14, color: '#6b7280' }}>No recipients selected.</div>
                      ) : (
                        selectedFilteredUsers.map(user => (
                          <UserRow
                            key={user.id}
                            type="button"
                            onClick={() => removeUser(user.id)}
                          >
                            <UserMeta>
                              <UserName>{user.name}</UserName>
                            </UserMeta>
                            <ActionBadge variant="remove">−</ActionBadge>
                          </UserRow>
                        ))
                      )}
                    </UserList>
                  </SelectionCard>
                </SelectionColumn>
              </SelectionGrid>
            ) : (
              <div style={{ padding: '12px 14px' }}>
                <SelectionTitle>Add a Note!</SelectionTitle>
                <div style={{ marginBottom: '1rem', color: '#6b7280' }}>
                  Sharing with {selectedUserIds.size} recipient{selectedUserIds.size === 1 ? '' : 's'}.
                  Add a short message to encourage them to check out the shared content.
                </div>
                <Form.TextArea
                  placeholder="Write a note to include in the resource sharing email message..."
                  value={personalMessage}
                  onChange={(event, data) => setPersonalMessage(data.value as string)}
                  style={{ minHeight: 220, width: '100%' }}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </Form>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={isSubmitting}>Close</Button>
        {!isTierOneOrganization && (
          <>
            {currentStep === 2 && (
              <Button onClick={() => setCurrentStep(1)} disabled={isSubmitting}>Back</Button>
            )}
            <Button
              color="blue"
              loading={isSubmitting}
              disabled={
                isSubmitting ||
                isUndefined(selectedOrganization) ||
                selectedUserIds.size === 0
              }
              onClick={currentStep === 1 ? handleNext : handleShare}
            >
              {currentStep === 1 ? 'Next' : 'Send'}
            </Button>
          </>
        )}
      </Modal.Actions>
    </PositionedModal>
  );
};

export default KnowledgeBaseContentShareModal;
